import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import useInterval from '../../utils/useInterval';
import PadColumn from './components/PadColumn';
import OrderQueueSidebar from './components/OrderQueueSidebar';
import Logo from '../../assets/images/MANNA_LOGO.png';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getLandingPads, subscribeToLiveAircraft } from '../../actions/liveAircraftActions';
import { getCells } from '../../actions/orderActions';
import { selectCells } from 'selectors/ordersSelector';
import { selectPads } from 'selectors/liveAircraftSelectors';
import { LandingZone, Order } from '@m4a3/api-client';
import { LandingPad } from '../../models/landing-pad.interface';
import { RootState } from 'store/configureStore';
import {
  LoadingOrderOpsStatus,
  OrdersForOpsStatuses
} from 'constants/orderConstants';
import { inactiveStatuses, flyingStatuses } from '../../constants';
import { useSearchParams } from 'react-router-dom';
import { selectLiveAircraftData } from '../../selectors/liveAircraftSelectors';
import { useOrderSSE2 } from '../../hooks/useOrderSSE2';
import { logToDatadog } from '../../services/datadog';
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { getAllLandingZonesForCell } from '../../utils/getAllLandingZonesForCell';
import packageJson from '../../../package.json';

// Check if File System Access API is supported (Chromium browsers only)
const supportsFileSystemAccess = typeof window !== 'undefined' && 'showSaveFilePicker' in window;

interface AircraftTelemetry {
  batteryVolts?: number;
  uavState?: string;
  lastTelemetry?: number; // Unix timestamp of last telemetry update
  mission?: { missionId?: string };
  [key: string]: any;
}

interface AlertRule {
  id: string;
  check: (telemetry: AircraftTelemetry, order?: Order) => boolean;
  message: (telemetry: AircraftTelemetry) => string;
  type: 'warning' | 'error' | 'info';
}

const BATTERY_ALERT_THRESHOLD = 51;

const aircraftDisplayEquality = (prev: any, next: any): boolean => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of prevKeys) {
    const p = prev[key];
    const n = next[key];
    if (p?.uavState !== n?.uavState) return false;
    if ((p?.batteryVolts <= BATTERY_ALERT_THRESHOLD) !== (n?.batteryVolts <= BATTERY_ALERT_THRESHOLD)) return false;
    if (p?.mission?.missionId !== n?.mission?.missionId) return false;
  }
  return true;
};

const ordersEquality = (prev: Order[], next: Order[]): boolean => {
  if (prev === next) return true;
  if (!prev || !next || prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i];
    const n = next[i];
    if (p === n) continue;
    if (p.id !== n.id ||
        p.status !== n.status ||
        p.originLandingPadId !== n.originLandingPadId ||
        p.missionId !== n.missionId ||
        p.cellId !== n.cellId ||
        (p as any).landingZoneId !== (n as any).landingZoneId ||
        (p as any).takeOffTimeMs !== (n as any).takeOffTimeMs ||
        (p as any).storePreparedDate !== (n as any).storePreparedDate ||
        (p as any).modifyDate !== (n as any).modifyDate) {
      return false;
    }
  }
  return true;
};

const blanchCellId = 7;
const blanchZoneId = 'df11cb36-3129-4824-81bb-dd470cd21837';
const validOrderStatuses = new Set(Object.values(OrdersForOpsStatuses));
const enumSortingValues = Object.values(OrdersForOpsStatuses);
const TELEM_DEBUG = true;

const LoaderApp = () => {
  const dispatch = useDispatch();

  const pads = useSelector(selectPads);
  const orders = useSelector((state: RootState) => state.orders.ordersToFilter, ordersEquality);

  const cells = useSelector(selectCells);
  const store = useStore();
  const aircraftData = useSelector(selectLiveAircraftData, aircraftDisplayEquality);

  const [landingZones, setLandingZones] = useState<LandingZone[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedCellId = parseInt(searchParams.get('cell')) || blanchCellId;
  const selectedLandingZone = searchParams.get('zone') || blanchZoneId;

  // Track alerts for each pad
  const [padAlerts, setPadAlerts] = useState<{[key: string]: {[key: string]: string}}>({});
  
  // Track what orders are assigned to each pad locally - this will allow us to compare new states against old states and notify the user if the order has changed pads 
  const [padAssignmentForOrdersLocally, setPadAssignmentForOrdersLocally] = useState<Record<number, Order[]>>({});
  
  // Track pad change notifications
  const [padChangedNotifications, setPadChangedNotifications] = useState<Record<string, { orderId: string; fromPadId: number; fromPadName: string; toPadId: number; toPadName: string }>>({});
  
  // Audio ref for notification sounds
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Track previous notification IDs to detect new ones
  const previousPadChangeNotificationIdsRef = useRef<Set<string>>(new Set());
  
  // Track previous alert keys to detect new alerts
  const previousAlertKeysRef = useRef<Set<string>>(new Set());
  
  // Track previous aircraft statuses to detect ABORT transitions
  const previousAircraftStatusesRef = useRef<Record<string, string>>({});
  
  // Track stale telemetry alerts separately (since they need independent checking)
  const [staleTelemetryAlerts, setStaleTelemetryAlerts] = useState<Record<string, string>>({});
  
  // Debug recording state
  const [isRecording, setIsRecording] = useState(false);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const fileHandleRef = useRef<any>(null);
  const writableRef = useRef<any>(null);
  
  // Debug: Pause telemetry updates (for testing stale telemetry alerts)
  const [isTelemetryPaused, setIsTelemetryPaused] = useState(false);
  
  // Keep a ref with the latest telemetry data for the stale-check interval.
  // Uses a store subscription so it updates on every tick without triggering re-renders.
  const aircraftDataRef = useRef(aircraftData);
  
  const padsRef = useRef(pads);
  padsRef.current = pads;
  
  const selectedLandingZoneRef = useRef(selectedLandingZone);
  selectedLandingZoneRef.current = selectedLandingZone;
  
  const { isConnected, error } = useOrderSSE2(selectedCellId, selectedLandingZone);

  const allPads = useMemo(
    () => pads.filter(pad => pad.landingZoneId === selectedLandingZone),
    [pads, selectedLandingZone]
  );

  // Define alert rules
  const alertRules: AlertRule[] = [
    {
      id: 'battery_voltage',
      check: (telemetry, order) => {

        // Only show if order has a missionId
        if (!order || !order.missionId) {
          return false;
        }
        // Don't show battery warning if order is in inactive or flying status
        if (order && [...inactiveStatuses, ...flyingStatuses].includes(order.status)) {
          return false;
        }

        return telemetry && telemetry.batteryVolts <= BATTERY_ALERT_THRESHOLD && telemetry.uavState === 'MAINTENANCE';
      },
      message: (telemetry) => telemetry ? `Change cassette\n battery below 51V)` : '',
      type: 'warning'
    },

  ];

  useInterval(() => {
    dispatch(getLandingPads());
  }, 10000);

  // Check for stale telemetry every 2.5 seconds using the telemetry's own timestamp
  // Uses refs to always get the latest data (avoids stale closure issues)
  useInterval(() => {
    const now = Date.now();
    const STALE_THRESHOLD_MS = 15000; // 15 seconds
    const newStaleAlerts: Record<string, string> = {};
    
    // Read current values from refs (never from closures)
    const currentAircraftData = aircraftDataRef.current;
    const currentPads = padsRef.current;
    const currentLandingZone = selectedLandingZoneRef.current;
    
    // Compute allPads inside the interval using ref values
    const currentAllPads = currentPads?.filter(pad => 
      pad.landingZoneId === currentLandingZone
    ) || [];
    
    // Check each aircraft we're tracking on current pads
    currentAllPads.forEach(pad => {
      if (pad.aircraft && currentAircraftData) {
        const aircraftKey = pad.aircraft.toString();
        const telemetry = currentAircraftData[aircraftKey] as AircraftTelemetry;
        
        // Use the telemetry's own lastTelemetry timestamp if available
        const lastTelemetryTime = telemetry?.lastTelemetry;
        
        if (lastTelemetryTime) {
          const timeSinceUpdate = now - lastTelemetryTime;
          if (timeSinceUpdate >= STALE_THRESHOLD_MS) {
            logToDatadog('setting_stale_telemetry_alert', { aircraftKey: aircraftKey, timeSinceUpdate: timeSinceUpdate });
            
            newStaleAlerts[aircraftKey] = `Last telemetry\n${formatTimeAgo(timeSinceUpdate)}`;
          }
        }
      }
    });
    
    setStaleTelemetryAlerts(prev => {
      const prevKeys = Object.keys(prev).sort().join(',');
      const newKeys = Object.keys(newStaleAlerts).sort().join(',');
      if (prevKeys === newKeys) return prev;
      return newStaleAlerts;
    });
  }, 2500);

  // Write debug snapshot to file every 5 seconds when recording
  useInterval(async () => {
    if (!isRecording || !writableRef.current) return;

    const snapshot = {
      type: 'snapshot',
      timestamp: Date.now(),
      isoTime: new Date().toISOString(),
      cellId: selectedCellId,
      landingZone: selectedLandingZone,
      pads: allPads.map(pad => {
        const telemetry = getAircraftTelemetry(pad.aircraft);
        const ordersForPad = getCurrentAndUpcomingOrdersForPad(orders, pad, telemetry?.uavState === 'ABORT');
        
        return {
          padName: pad.name,
          padId: pad.id,
          pad: JSON.parse(JSON.stringify(pad)),
          aircraftTelemetry: telemetry ? JSON.parse(JSON.stringify(telemetry)) : null,
          orders: ordersForPad.map(o => JSON.parse(JSON.stringify(o))),
          alerts: pad.aircraft ? padAlerts[pad.aircraft.toString()] : null,
        };
      }),
      staleTelemetryAlerts: JSON.parse(JSON.stringify(staleTelemetryAlerts)),
      padChangedNotifications: JSON.parse(JSON.stringify(padChangedNotifications)),
    };

    try {
      await writableRef.current.write(JSON.stringify(snapshot) + '\n');
    } catch (err) {
      console.error('Failed to write snapshot:', err);
      setIsRecording(false);
    }
  }, 5000);

  useEffect(() => {
    if (!isTelemetryPaused) {
      aircraftDataRef.current = (store.getState() as RootState).liveAircraft.liveData;
    }
    const unsubscribe = store.subscribe(() => {
      if (!isTelemetryPaused) {
        aircraftDataRef.current = (store.getState() as RootState).liveAircraft.liveData;
      }
    });
    return unsubscribe;
  }, [store, isTelemetryPaused]);

  useEffect(() => {
    if (!isConnected && error) {
      logToDatadog('loader_app_SSE_disconnected');
    } else {
      logToDatadog('loader_app_SSE_connected');
    }
  }, [isConnected, error]);

  useEffect(() => {
    dispatch(getCells());
    dispatch(getLandingPads()); // Get landing pads once on mount
    
    // Initialize audio element
    audioRef.current = new Audio(require('../../assets/loaderAppAlert.wav'));
  }, [dispatch]);

  useEffect(() => {

    if (cells) {
      dispatch(getLandingPads());

      getAllLandingZonesForCell(cells)
      .then(res => {
        setLandingZones(res);
      })
      .catch(err => console.error('Error getting landing zones:', err));
    }
  }, [selectedCellId, cells]);

  useEffect(() => {
    if (TELEM_DEBUG) {
      console.log('Connecting to live aircraft for cell:', selectedCellId);
    }
    dispatch(subscribeToLiveAircraft());

    return () => {
      // Cleanup handled by action
    };
  }, []);  // Only on mount

  // Auto-recovery: If we have pads with aircraft but no telemetry data, try reconnecting
  useEffect(() => {
    const hasPadsWithAircraft = allPads.some(pad => pad.aircraft);
    const hasNoTelemetryData = !aircraftData || Object.keys(aircraftData).length === 0;
    
    if (hasPadsWithAircraft && hasNoTelemetryData) {
      // Debounce reconnection attempts - wait 5 seconds before trying
      const timeoutId = setTimeout(() => {
        logToDatadog('telemetry_auto_reconnect_attempt', { selectedCellId, selectedLandingZone });
        dispatch(subscribeToLiveAircraft());
      }, 5000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [aircraftData, allPads]);

  // Effect to check alerts for orders currently on pads
  useEffect(() => {
    if (!aircraftData) return;
    
    const newPadAlerts: {[key: string]: {[key: string]: string}} = {};
    
    const ordersOnPads = orders.filter(order => 
      order.originLandingPadId && 
      order.cellId?.toString() === selectedCellId?.toString()
    );
    
    ordersOnPads.forEach(order => {
      const pad = pads.find(p => p.id === order.originLandingPadId);
      if (!pad?.aircraft) return;
      
      const telemetry = aircraftData[pad.aircraft.toString()] as AircraftTelemetry;
      if (!telemetry) return;
      
      const aircraftKey = pad.aircraft.toString();
      const alerts = getAlertsForAircraft(telemetry, order, pad.aircraft);
      
      if (Object.keys(alerts).length > 0) {
        newPadAlerts[aircraftKey] = alerts;
      }
    });
    
    setPadAlerts(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newPadAlerts)) return prev;
      return newPadAlerts;
    });
  }, [aircraftData, orders, pads, selectedCellId]);
  
  // Clear pad assignment tracking when cell or landing zone changes
  useEffect(() => {
    setPadAssignmentForOrdersLocally({});
    setPadChangedNotifications({});
    setPadAlerts({});
    setStaleTelemetryAlerts({});
    previousPadChangeNotificationIdsRef.current = new Set();
    previousAlertKeysRef.current = new Set();
    previousAircraftStatusesRef.current = {};
  }, [selectedCellId, selectedLandingZone]);

  // Play sound when new notifications are added
  useEffect(() => {
    const currentPadChangeNotificationIds = new Set(Object.keys(padChangedNotifications));
    const previousPadChangeNotificationIds = previousPadChangeNotificationIdsRef.current;
    
    // Check if there are any new notification IDs
    const hasNewPadChangeNotifications = Array.from(currentPadChangeNotificationIds).some(
      id => !previousPadChangeNotificationIds.has(id)
    );
    
    if (hasNewPadChangeNotifications && currentPadChangeNotificationIds.size > 0) {

      logToDatadog('attempting_to_play_pad_change_sound');
      // Play the sound
      if (audioRef.current) {

        logToDatadog('playing_pad_change_sound');

        audioRef.current.play().catch(error => {
          logToDatadog('loader_app_notification_sound_error', { error: error?.message });
        });
      }
    }
    
    // Update the ref with current notification IDs
    previousPadChangeNotificationIdsRef.current = currentPadChangeNotificationIds;
  }, [padChangedNotifications]);
  
  // Play sound when new alerts are added
  useEffect(() => {
    // Create unique keys for each alert (aircraft + alert ID)
    const currentAlertKeys = new Set<string>();
    Object.entries(padAlerts).forEach(([aircraftKey, alerts]) => {
      Object.keys(alerts).forEach(alertId => {
        currentAlertKeys.add(`${aircraftKey}_${alertId}`);
      });
    });
    
    const previousAlertKeys = previousAlertKeysRef.current;
    
    // Check if there are any new alert keys
    const hasNewAlerts = Array.from(currentAlertKeys).some(
      key => !previousAlertKeys.has(key)
    );
    
    if (hasNewAlerts && currentAlertKeys.size > 0) {
      // Play the sound
      if (audioRef.current) {
        audioRef.current.play().catch(error => {
          logToDatadog('loader_app_alert_sound_error', { error: error?.message });
        });
      }
    }
    
    // Update the ref with current alert keys
    previousAlertKeysRef.current = currentAlertKeys;
  }, [padAlerts]);
  
  // Track aircraft status transitions to detect ABORT and play sound
  useEffect(() => {
    if (!aircraftData) return;
    
    const previousStatuses = previousAircraftStatusesRef.current;
    const currentStatuses: Record<string, string> = {};
    let hasAbortTransition = false;
    
    Object.entries(aircraftData).forEach(([aircraftNumber, telemetry]) => {
      const typedTelemetry = telemetry as AircraftTelemetry;
      
      if (typedTelemetry?.uavState) {
        const currentStatus = typedTelemetry.uavState;
        const previousStatus = previousStatuses[aircraftNumber];
        
        currentStatuses[aircraftNumber] = currentStatus;
        
        // Check for ABORT transition
        if (currentStatus === 'ABORT' && previousStatus && previousStatus !== 'ABORT') {
          logToDatadog('attempting_to_play_abort_sound', { aircraftNumber: aircraftNumber });
          console.log(`Aircraft ${aircraftNumber} transitioned from ${previousStatus} to ABORT`);
          hasAbortTransition = true;
        }
      }
    });
    
    // Play sound if any aircraft transitioned to ABORT
    if (hasAbortTransition && audioRef.current) {
      logToDatadog('playing_abort_sound');
      audioRef.current.play().catch(error => {
        logToDatadog('loader_app_abort_sound_error', { error: error?.message });
      });
    }
    
    previousAircraftStatusesRef.current = currentStatuses;
  }, [aircraftData]);

  // Update tracking and clear stale data when orders or pads change
  useEffect(() => {
    // First, build the new tracking state for all pads
    const newPadAssignments: Record<number, Order[]> = {};
    const newNotifications: Record<string, { orderId: string; fromPadId: number; fromPadName: string; toPadId: number; toPadName: string }> = {};
    
    // Filter existing tracking to only include valid orders
    Object.entries(padAssignmentForOrdersLocally).forEach(([padIdKey, trackedOrders]) => {
      const validOrders = trackedOrders.filter(order => 
        order.cellId === selectedCellId &&
        validOrderStatuses.has(order.status as OrdersForOpsStatuses)
      );
      if (validOrders.length > 0) {
        newPadAssignments[parseInt(padIdKey)] = validOrders;
      }
    });
    
    // Filter existing notifications to only include valid orders
    Object.entries(padChangedNotifications).forEach(([notificationKey, notification]) => {
      const order = orders.find(o => o.orderShortId === notification.orderId);
      if (order && 
          order.cellId === selectedCellId && 
          validOrderStatuses.has(order.status as OrdersForOpsStatuses)) {
        // Clear notification if order has returned to the pad it originally left from
        const orderIsBackOnOriginalPad = order.originLandingPadId === notification.fromPadId;
        if (!orderIsBackOnOriginalPad) {
          newNotifications[notificationKey] = notification;
        }
      }
    });
    
    // Now check each pad for changes
    allPads.forEach(pad => {
      const ordersForPad = orders.filter(order => {
        return (
          order.cellId === selectedCellId &&
          order?.originLandingPadId === pad.id &&
          validOrderStatuses.has(order.status as OrdersForOpsStatuses)
        );
      });
      
      ordersForPad.sort(sortOrdersByStatusAndTime);
      
      // Get previously tracked orders for this pad
      const previouslyTrackedOrders = newPadAssignments[pad.id] || [];
      
      // Create a set of current order IDs for quick lookup
      const currentOrderIds = new Set(ordersForPad.map(o => o.orderShortId));
      
      // Find orders that were on this pad but are no longer on it
      const ordersThatLeft = previouslyTrackedOrders.filter(
        prevOrder => !currentOrderIds.has(prevOrder.orderShortId)
      );
      
      // For each order that left, check if it's now on a different pad
      ordersThatLeft.forEach(oldOrder => {
        // Find the current version of this order to see where it is now
        const currentOrder = orders.find(o => o.orderShortId === oldOrder.orderShortId);
        
        // Check if this order is now on a different pad
        if (currentOrder && currentOrder.originLandingPadId && currentOrder.originLandingPadId !== pad.id) {
          // Find the destination pad to get its name
          const toPad = allPads.find(p => p.id === currentOrder.originLandingPadId);
          
          // Create unique key for this specific movement (order + destination pad)
          const notificationKey = `${currentOrder.orderShortId}_to_${currentOrder.originLandingPadId}`;
          
          newNotifications[notificationKey] = {
            orderId: currentOrder.orderShortId,
            fromPadId: pad.id,
            fromPadName: pad.name,
            toPadId: currentOrder.originLandingPadId,
            toPadName: toPad?.name || `Pad ${currentOrder.originLandingPadId}`
          };
        }
      });
      
      // Update the tracking for this pad
      newPadAssignments[pad.id] = ordersForPad;
    });
    
    setPadAssignmentForOrdersLocally(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newPadAssignments)) return prev;
      return newPadAssignments;
    });
    setPadChangedNotifications(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newNotifications)) return prev;
      return newNotifications;
    });
  }, [orders, allPads.length, selectedCellId]);
  
  /** 
   * Helper functions to find orders on pads ----------------------------------------------------
   */

  const findPadByAircraftNumber = (aircraftNumber: number, pads: LandingPad[], cellId: string | number): LandingPad | undefined => {
    return pads.find(pad =>
      pad.aircraft === aircraftNumber &&
      pad.cellId.toString() === cellId.toString()
    );
  };

  const findOrderByPad = (pad: LandingPad, orders: Order[], cellId: string | number): Order | undefined => {
    return orders.find(order =>
      order?.originLandingPadId === pad.id &&
      order.cellId.toString() === cellId.toString()
    );
  };

  const findOrderByAircraftNumber = (aircraftNumber: number, pads: LandingPad[], orders: Order[], cellId: string | number): Order | undefined => {
    const pad = findPadByAircraftNumber(aircraftNumber, pads, cellId);
    if (pad) {
      return findOrderByPad(pad, orders, cellId);
    }
    return undefined;
  };

  const findPadByOrder = (order: Order, pads: LandingPad[]): LandingPad | undefined => {
    return pads.find(pad => pad.id === order?.originLandingPadId);
  };

  // Helper to format time ago
  const formatTimeAgo = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) {
      return `${seconds} sec ago`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    let zoneValue = JSON.parse(event.target.value);
    newSearchParams.set('zone', zoneValue.id);
    newSearchParams.set('cell', zoneValue.cellId);
    setSearchParams(newSearchParams);
  };

  /** 
   * Checking pad alerts ------------------------------------------------------------
  */

  const getAlertsForAircraft = (
    telemetry: AircraftTelemetry,
    order: Order,
    aircraftNumber: number
  ): {[key: string]: string} => {
    const alerts: {[key: string]: string} = {};

    alertRules.forEach(rule => {
      if (rule.check(telemetry, order)) {
        logToDatadog('setting_low_battery_alert', {
          aircraftNumber,
          telemetry: telemetry.batteryVolts ?? 'Unknown',
          orderId: order.id,
        });
        alerts[rule.id] = rule.message(telemetry);
      }
    });

    return alerts;
  };

  /** 
   * Assigning orders to pads ------------------------------------------------------------
  */

  const getAircraftTelemetry = (aircraftNumber?: number | null): AircraftTelemetry | null => {
    if (!aircraftNumber) return null;
    return aircraftData[aircraftNumber.toString()] as AircraftTelemetry;
  };

  const sortOrdersByStatusAndTime = useCallback((a: Order, b: Order) => {
    if (a.status !== b.status) {
      return (
        enumSortingValues.indexOf(a.status as OrdersForOpsStatuses) -
        enumSortingValues.indexOf(b.status as OrdersForOpsStatuses)
      );
    }
    return (
      new Date(a.storePreparedDate).getTime() -
      new Date(b.storePreparedDate).getTime()
    );
  }, []);

  const getCurrentAndUpcomingOrdersForPad = (
    orders: Order[],
    pad: LandingPad,
    isAircraftAborting: boolean = false
  ): Order[] => {

    let activeOrdersAssignedToPad = orders.filter(order => {
      return (
        order.cellId === selectedCellId &&
        order?.originLandingPadId === pad.id &&
        validOrderStatuses.has(order.status as OrdersForOpsStatuses) 
      );
    });

    activeOrdersAssignedToPad.sort(sortOrdersByStatusAndTime); //Sort the orders so we can grab the next in queue and following order from the array 

    if (isAircraftAborting) { //This is a hack given aborted orders are marked as complete or a non aborted status immediately 
      //If the aircraft is aborting show the order for the mission that's currently being aborted
      const padTelemetry = getAircraftTelemetry(pad.aircraft);
      const abortingMissionId = padTelemetry?.mission?.missionId;
      
      if (abortingMissionId) {
        // Find the specific order that matches the aborting mission
        const abortingOrder = orders.find(order => 
          order.missionId === abortingMissionId &&
          order.cellId === selectedCellId &&
          order?.originLandingPadId === pad.id
        );
        
        if (abortingOrder) {
          // Filter out the aborting order if it's already in the list to avoid duplicates
          activeOrdersAssignedToPad = [
            abortingOrder, 
            ...activeOrdersAssignedToPad.filter(order => order.id !== abortingOrder.id)
          ];
        }
      }
    }

    return activeOrdersAssignedToPad ?? []; //Return the orders assigned to this pad 
  };

  const getLoaderScreenStatusFromOrderStatus = (order: Order, telemetry: AircraftTelemetry): LoadingOrderOpsStatus | undefined => {

    const droneInStandby = telemetry?.uavState === 'STANDBY';
    const droneInMaintenance = telemetry?.uavState === 'MAINTENANCE';
    const droneInMission = telemetry?.uavState === 'MISSION';
    const droneAborting = telemetry?.uavState === 'ABORT';
    const droneMotorTesting = telemetry?.uavState === 'MOTORSTEST';

    // ------------------------------------------------------------
    
    const orderStatusReceived = order.status === 'RECEIVED_FROM_STORE';// We allow received orders to be actioned to offset any delays with MC assigning a route 
    const orderStatusReady = order.status === 'READY'; 
    const orderStatusDispatched = order.status === 'DISPATCHED';
    const orderStatusDelivered = order.status === 'DELIVERED';

    // ------------------------------------------------------------

    const missionCreated = order['missionStatus'] === 'CREATED';
    const missionProposed = order['missionStatus'] === 'PROPOSED'
    const missionHold = order['missionStatus'] === 'HOLD'
    const missionInProgress = order['missionStatus'] === 'IN_PROGRESS'

    logToDatadog('getting_loader_screen_status', { 
      orderId: order.id, 
      order: order, 
      telemetry: telemetry,
      droneStatus: telemetry?.uavState ?? 'Unknown',
      orderStatus: order.status,
      misionStatus: order['missionStatus'],
    });

    // Begin assigning sates 

    //Return nothing if motor testing
    if (droneMotorTesting) {
      return undefined;
    }

    // LOAD_NOW 
    if (orderStatusReceived
      && (droneInStandby || droneInMaintenance)
      && (missionProposed || missionCreated)
    ) {
      return LoadingOrderOpsStatus.LOAD_NOW;
    }

    // LOAD NOW
    if (order.missionId 
      && orderStatusReady
      && (droneInStandby || droneInMaintenance)
       && missionHold) {
      return LoadingOrderOpsStatus.LOAD_NOW;
    }
    
    //DISPATCHED
    if ((orderStatusDispatched || orderStatusDelivered) && missionInProgress && droneInMission) { //Dispatched but order not complete 
      return order.status as LoadingOrderOpsStatus;
    }

    //CHANGE CASSETTE ON LANDING - An order could have successfully delivered but the drone aborted after delivery - always show change cassette on landing for these orders
    if ((order.status === 'ABORTED' 
      || order.status === 'COMPLETE' 
      || order.status === 'DISPATCHED' 
      || order.status === 'DELIVERED') && droneAborting) { //I've seen in testing orderStatus - Dispatched and Drone status abort
      //We can probably rely on droneAborting here and ignore order['missionStatus'] === 'ABORTED' || order['missionStatus'] === 'MANUAL'
      //Similarly we can ignore order['missionStatus'] === 'COMPLETED' as we can rely on order.status === 'COMPLETE'
      return LoadingOrderOpsStatus.CHANGE_CASSETTE;
    }

    // DELIVERED OR COMPLETE
    if (order.status === 'DELIVERED' || order.status === 'COMPLETE') {  //Maybe here we need to use deliveryed as RETURNING 
      return order.status as LoadingOrderOpsStatus;
    }

    logToDatadog('no_loader_screen_status_found', { 
      orderId: order.id, 
      order: order, 
      telemetry: telemetry,
      droneStatus: telemetry?.uavState ?? 'Unknown',
      orderStatus: order.status,
      misionStatus: order['missionStatus'],
    });

    return undefined;
  };

  /** 
   * Calculate sidebar orders ------------------------------------------------------------
  */

  const atBaseOrders = useMemo((): Order[] => {
    const result: Order[] = [];
    
    allPads.forEach(pad => {
      const ordersForPad = getCurrentAndUpcomingOrdersForPad(orders, pad);
      const upcoming = ordersForPad.slice(1);
      result.push(...upcoming);
    });
    
    const unassignedOrders = orders.filter(order => {
      return (
        order.cellId === selectedCellId &&
        (order as any).landingZoneId === selectedLandingZone &&
        (order.status === 'RECEIVED_FROM_STORE' || order.status === 'READY') &&
        (!order.originLandingPadId || order.originLandingPadId === null)
      );
    });
    
    result.push(...unassignedOrders);
    result.sort(sortOrdersByStatusAndTime);
    
    return result;
  }, [orders, allPads, selectedCellId, selectedLandingZone, sortOrdersByStatusAndTime]);

  const notAtBaseOrders = useMemo((): Order[] => {
    return orders.filter(order => {
      return (
        order.cellId === selectedCellId &&
        (order as any).landingZoneId === selectedLandingZone &&
        (order.status === 'STORE_PREPARED')
      );
    }).sort(sortOrdersByStatusAndTime);
  }, [orders, selectedCellId, selectedLandingZone, sortOrdersByStatusAndTime]);

  const getAllPadColumns = (): React.ReactNode => {

    return allPads.map(pad => { 

      const telemetry = getAircraftTelemetry(pad.aircraft);

      const isAircraftAborting = telemetry?.uavState === 'ABORT';

      let ordersForPad = getCurrentAndUpcomingOrdersForPad(
        orders,
        pad,
        isAircraftAborting
      );
      
      // Stale telemetry alert takes priority - if telemetry is stale, other alerts (like battery voltage) are unreliable
      const aircraftKey = pad.aircraft?.toString();
      const regularAlerts = aircraftKey ? padAlerts[aircraftKey] : undefined;
      const staleAlert = aircraftKey ? staleTelemetryAlerts[aircraftKey] : undefined;
      
      // If stale telemetry, only show that alert (other alerts are based on stale data)
      const alerts = staleAlert 
        ? { stale_telemetry: staleAlert }
        : regularAlerts;

      const orderOneShortId = ordersForPad[0]?.orderShortId;
      const orderTwoShortId = ordersForPad[1]?.orderShortId;

      const switchedToPadName = findPadByOrder(ordersForPad[0], allPads)?.name;
      
      return (
        <div key={pad.id}>
          <PadColumn
            key={pad.name}
            padName={pad.name}
            padId={pad.id}
            pad={pad}
            order1ShortId={orderOneShortId}
            order1Status={
              ordersForPad[0]
                ? getLoaderScreenStatusFromOrderStatus(ordersForPad[0], telemetry)
                : undefined
            }
            order1TimeSinceOrderModified={
              ordersForPad[0]?.modifyDate
                ? ordersForPad[0]?.modifyDate 
                : undefined
            }
            order2={ordersForPad[1]}
            order2ShortId={orderTwoShortId}
            switchedToPadName={switchedToPadName}
            aircraftStatus={telemetry?.uavState}  
            order1={ordersForPad[0]}
            alerts={alerts}
            padChangedNotifications={padChangedNotifications}
            isOrder1Resending={ordersForPad[0]?.originalOrderId ? true : false}
          />
        </div>
      );
    });
  };

  // Debug Recording Functions
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleCloseContextMenu = () => {
    setShowContextMenu(false);
  };

  const startRecording = async () => {
    setShowContextMenu(false);
    
    if (!supportsFileSystemAccess) {
      setShowBrowserModal(true);
      return;
    }

    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: `loader-debug-${new Date().toISOString().slice(0, 10)}.jsonl`,
        types: [{
          description: 'JSON Lines',
          accept: { 'application/jsonl': ['.jsonl'] },
        }],
      });
      
      const writable = await handle.createWritable();
      fileHandleRef.current = handle;
      writableRef.current = writable;
      setIsRecording(true);
      
      // Write header
      const header = {
        type: 'session_start',
        timestamp: Date.now(),
        isoTime: new Date().toISOString(),
        version: packageJson.version,
        cellId: selectedCellId,
        landingZone: selectedLandingZone,
      };
      await writable.write(JSON.stringify(header) + '\n');
      
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  const stopRecording = async () => {
    setShowContextMenu(false);
    
    if (writableRef.current) {
      // Write footer
      const footer = {
        type: 'session_end',
        timestamp: Date.now(),
        isoTime: new Date().toISOString(),
      };
      await writableRef.current.write(JSON.stringify(footer) + '\n');
      await writableRef.current.close();
    }
    
    writableRef.current = null;
    fileHandleRef.current = null;
    setIsRecording(false);
  };

  return (
    <div className="fixed inset-0 flex overflow-hidden" onContextMenu={handleContextMenu} onClick={handleCloseContextMenu}>
      {/* Custom Context Menu */}
      {showContextMenu && (
        <div 
          className="fixed bg-white border border-gray-300 rounded shadow-lg py-1 z-50"
          style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
        >
          {isRecording ? (
            <button 
              className="px-4 py-2 hover:bg-gray-100 w-full text-left flex items-center gap-2"
              onClick={stopRecording}
            >
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Stop Recording
            </button>
          ) : (
            <button 
              className="px-4 py-2 hover:bg-gray-100 w-full text-left"
              onClick={startRecording}
            >
              🔴 Start Recording
            </button>
          )}
          <button 
            className="px-4 py-2 hover:bg-gray-100 w-full text-left border-t border-gray-200"
            onClick={() => {
              setIsTelemetryPaused(!isTelemetryPaused);
              setShowContextMenu(false);
            }}
          >
            {isTelemetryPaused ? '▶️ Resume Telemetry' : '⏸️ Pause Telemetry'}
          </button>
        </div>
      )}

      {/* Browser Compatibility Modal */}
      <Dialog open={showBrowserModal} onClose={() => setShowBrowserModal(false)}>
        <DialogTitle>Browser Not Supported</DialogTitle>
        <DialogContent>
          <p className="mb-2">
            Debug recording requires the File System Access API, which is only available in <strong>Chrome</strong> or <strong>Edge</strong>.
          </p>
          <p>
            Please open this page in Chrome or Edge to use the recording feature.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBrowserModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full flex items-center gap-2 z-50">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          Recording
        </div>
      )}
      
      {/* Telemetry Paused Indicator */}
      {isTelemetryPaused && (
        <div className="fixed top-4 right-32 bg-orange-500 text-white px-3 py-1 rounded-full flex items-center gap-2 z-50">
          <span>⏸️</span>
          Telemetry Paused
        </div>
      )}

      {/* Sidebar */}
      <OrderQueueSidebar
        atBaseOrders={atBaseOrders}
        notAtBaseOrders={notAtBaseOrders}
      />
      
      {/* Main content */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="grid grid-cols-4 flex-grow min-h-0 overflow-auto bg-mannamono-bg">
          {getAllPadColumns()}
        </div>
        <footer className="border-t border-mannamono-line p-5 flex justify-between">
          <div className="flex gap-2 items-center">
            <img src={Logo} alt="Manna Drone Logo" className="max-h-5" />{' '}
            <span className="font-sans text-base">L O A D E R</span>
            <span className="font-sans text-sm text-gray-500 ml-2">v{packageJson.version}</span>
            
            {isConnected ? (
              <div className="flex items-center ml-4">
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <Typography variant="body1" className="text-green-700 font-medium">
                  Live Updates
                </Typography>
              </div>
            ) : (
              <div className="flex items-center ml-4">
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <Typography variant="body1" className="text-red-700 font-medium">
                  Disconnected
                </Typography>
              </div>
            )}
          </div>

          <select
            onChange={event => {
              handleSearchChange(event);
            }}
            className="text-lg">
            <option key={1}>Select a Landing Zone</option>
            {landingZones
              ? landingZones.map(zone => {
                  return (
                    <option
                      selected={zone.id === selectedLandingZone}
                      key={zone.id}
                      value={JSON.stringify(zone)}>
                      {zone.name}
                    </option>
                  );
                })
              : ''}
          </select>
        </footer>
      </div>
    </div>
  );
};

export default LoaderApp;